import classNames from 'classnames'
import { Variants, motion } from 'motion/react'
import {
  CSSProperties,
  PropsWithChildren,
  RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { BaseModalType, useModal } from '@context/ModalsContext'

import { SearchFieldProps } from '@ui/SearchField/SearchField'
import { Direction } from '@ui/common/layout.types'
import { Modal } from '@ui/components/Modal'

import { useWindowResize } from '@utils/domUtils'
import { getPortalRoot } from '@utils/portals'

import { useOnClickedOutside } from '@hooks/useOnClickedOutside'
import { useTooltipPosition } from '@hooks/useTooltipPosition'

import styles from './DropdownContent.module.scss'

const variants: Variants = {
  open: {
    opacity: 1,
    transition: { delay: 0.1, type: 'spring', bounce: 0, duration: 0.5 },
  },
  closed: {
    opacity: 0,
    transition: { type: 'spring', bounce: 0, duration: 0.25 },
  },
}

type ModalRenderContext = {
  search: string
  selectedIds: string[]
  toggle: (id: string) => void
  setSelectedIds: (ids: string[]) => void
}

interface DropdownContentProps {
  isOpen: boolean
  style?: CSSProperties
  label?: string
  isPagination?: boolean
  direction: Direction
  onClose: () => void
  triggerElement?: HTMLDivElement | null
  modalHeaderSearch?: SearchFieldProps
  modalFooter?: React.ReactNode | ((close: () => void) => React.ReactNode)
  modalCanSearch?: boolean
  modalInitialSelectedIds?: string[]
  onApplyOnClose?: (selectedIds: string[]) => void
  modalRender?: (ctx: ModalRenderContext) => React.ReactNode
  onModalSearchChange?: (value: string) => void
  isMultiSelect?: boolean
}

interface DropModalProps extends PropsWithChildren {
  label?: string
  modalHeaderSearch?: SearchFieldProps
  modalFooter?: React.ReactNode | ((close: () => void) => React.ReactNode)
  modalCanSearch?: boolean
  modalInitialSelectedIds?: string[]
  modalRender?: (ctx: ModalRenderContext) => React.ReactNode
  applyBridgeRef?: RefObject<string[] | null>
  onModalSearchChange?: (value: string) => void
}

const DropModal: BaseModalType<DropModalProps> = ({
  children,
  close,
  label,
  modalHeaderSearch,
  modalFooter,
  modalCanSearch,
  modalInitialSelectedIds,
  modalRender,
  applyBridgeRef,
  onModalSearchChange,
}) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>(modalInitialSelectedIds ?? [])

  useEffect(() => {
    if (applyBridgeRef) {
      applyBridgeRef.current = selectedIds
    }
  }, [applyBridgeRef, selectedIds])

  useEffect(() => {
    if (!isMobile) close()
  }, [isMobile, close])

  const toggle = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  return (
    <Modal>
      <Modal.Header
        search={
          modalCanSearch
            ? {
                value: search,
                onChange: (v: string) => {
                  setSearch(v)
                  modalHeaderSearch?.onChange?.(v)
                  onModalSearchChange?.(v)
                },
                label: modalHeaderSearch?.label,
                disabled: modalHeaderSearch?.disabled,
                canClear: modalHeaderSearch?.canClear,
              }
            : undefined
        }
      >
        {label}
      </Modal.Header>

      <Modal.Body onClick={(e) => e.stopPropagation()}>
        {modalRender ? modalRender({ search, selectedIds, toggle, setSelectedIds }) : children}
      </Modal.Body>

      <Modal.Footer>
        {modalFooter ? (
          typeof modalFooter === 'function' ? (
            modalFooter(close)
          ) : (
            modalFooter
          )
        ) : (
          <Modal.Footer.Secondary onClick={close}>{t('Close')}</Modal.Footer.Secondary>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export const DropdownContent = forwardRef<HTMLDivElement, PropsWithChildren<DropdownContentProps>>(
  (props, forwardedRef) => {
    const {
      isOpen,
      children,
      direction,
      style,
      onClose,
      label,
      isPagination = false,
      triggerElement,
      modalHeaderSearch,
      modalFooter,
      modalCanSearch,
      modalInitialSelectedIds,
      onApplyOnClose,
      modalRender,
      onModalSearchChange,
      isMultiSelect,
    } = props

    const portalRootRef = useRef<HTMLElement | null>(null)
    const triggerRef = useRef<HTMLDivElement | null>(null)
    const dropModal = useModal(DropModal)
    const isMobile = useWindowResize()

    const handleOutsideClick = useCallback(() => {
      if (isOpen && !isMobile) onClose()
    }, [onClose, isOpen, isMobile])

    const applyBridgeRef = useRef<string[] | null>(null)
    const bridgeActiveRef = useRef(false)
    const didOpenModalRef = useRef(false)
    const justOpenedRef = useRef(false)
    const justOpenedTimer = useRef<number | null>(null)
    const localRef = useOnClickedOutside<HTMLDivElement>(handleOutsideClick)

    useEffect(() => {
      if (!isMobile || !isOpen || didOpenModalRef.current) {
        return
      }

      const handleModalClose = () => {
        const selected = applyBridgeRef.current ?? []
        onApplyOnClose?.(selected)
        bridgeActiveRef.current = false
        didOpenModalRef.current = false
        onClose()
      }

      bridgeActiveRef.current = true
      didOpenModalRef.current = true
      justOpenedRef.current = true
      dropModal.open({
        onClose: handleModalClose,
        children,
        label,
        modalHeaderSearch,
        modalFooter,
        modalCanSearch,
        modalInitialSelectedIds,
        modalRender,
        applyBridgeRef,
        onModalSearchChange,
      })
      queueMicrotask(() => {
        bridgeActiveRef.current = false
      })
      if (justOpenedTimer.current) {
        clearTimeout(justOpenedTimer.current)
      }
      justOpenedTimer.current = window.setTimeout(() => {
        justOpenedRef.current = false
        justOpenedTimer.current = null
      }, 150)
    }, [
      isMobile,
      isOpen,
      children,
      dropModal,
      label,
      modalHeaderSearch,
      modalFooter,
      modalCanSearch,
      modalInitialSelectedIds,
      modalRender,
      onApplyOnClose,
      onClose,
      onModalSearchChange,
    ])

    const prevIsOpenRef = useRef(isOpen)
    useEffect(() => {
      prevIsOpenRef.current = isOpen

      const shouldSyncClose = !isOpen && dropModal.isOpen && didOpenModalRef.current

      if (!shouldSyncClose || justOpenedRef.current) return

      if (!bridgeActiveRef.current) {
        if (!isMobile && dropModal.isOpen) {
          dropModal.close()
        } else if (isMobile && !isMultiSelect && dropModal.isOpen) {
          dropModal.close()
        }
      }
      didOpenModalRef.current = false
    }, [dropModal, isOpen, isMobile, isMultiSelect])

    useEffect(() => {
      if (typeof forwardedRef === 'function') {
        forwardedRef(localRef.current)
      } else if (forwardedRef) {
        forwardedRef.current = localRef.current
      }
    }, [forwardedRef, localRef])

    if (!portalRootRef.current) {
      portalRootRef.current = getPortalRoot('DROPDOWN')
    }

    useEffect(() => {
      triggerRef.current = triggerElement ?? null
    }, [triggerElement])

    const { contentOrigin } = useTooltipPosition({
      boxRef: localRef,
      triggerRef,
      contentDirection: direction,
      isOpen: isOpen && !isMobile,
      distance: '0.5rem',
    })

    if (!isOpen || isMobile) {
      return null
    }

    const triggerWidth = triggerRef.current?.getBoundingClientRect().width

    return createPortal(
      <motion.div
        onClick={(e) => e.stopPropagation()}
        animate={isOpen ? 'open' : 'closed'}
        initial='closed'
        variants={variants}
        ref={localRef}
        className={classNames(styles.dropdownContent, {
          [styles.open]: isOpen,
          [styles.pagination]: isPagination,
        })}
        style={{
          ...style,
          top: contentOrigin?.top,
          left: contentOrigin?.left,
          transform: contentOrigin?.transform,
          minWidth: triggerWidth,
        }}
      >
        {children}
      </motion.div>,
      portalRootRef.current!
    )
  }
)
